import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { formatINR } from '../data/products';
import { 
  Search, 
  Heart, 
  ShoppingCart, 
  Menu, 
  X, 
  Gem, 
  Sparkles,
  ShieldCheck,
  ChevronDown,
  Layers,
  Scale,
  Lock,
  Smartphone,
  Download
} from 'lucide-react';

export default function Navbar() {
  const navigate = useNavigate();
  const searchContainerRef = useRef(null);
  const { 
    storeSettings,
    cartCount, 
    wishlist, 
    searchQuery,
    setSearchQuery,
    activeCategory,
    setActiveCategory,
    setIsWastageModalOpen,
    categories,
    products,
    masterMetalRates,
    calculateProductPrice,
    isAdminAuthenticated,
    logoutAdmin,
    currentCustomer,
    isCustomerLoggedIn,
    openCustomerAuthModal,
    logoutCustomer
  } = useShop();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [categoriesDropdownOpen, setCategoriesDropdownOpen] = useState(false);
  const [moreDropdownOpen, setMoreDropdownOpen] = useState(false);
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setIsSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Compute live search matches
  const matchingJewels = (products || []).filter(p => {
    if (!searchQuery || !searchQuery.trim() || !p) return false;
    const q = searchQuery.toLowerCase().trim();
    return (
      (p.name || '').toLowerCase().includes(q) ||
      (p.category || '').toLowerCase().includes(q) ||
      (p.metal || '').toLowerCase().includes(q) ||
      (p.subtitle || '').toLowerCase().includes(q) ||
      (p.sku || '').toLowerCase().includes(q)
    );
  }).slice(0, 6);

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    if (!searchQuery.trim()) return;
    setActiveCategory('all');
    setIsSearchFocused(false);
    setMobileSearchOpen(false);
    navigate('/category/all');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectSearchResult = (productId) => {
    setIsSearchFocused(false);
    setMobileSearchOpen(false);
    navigate(`/product/${productId}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const activeCategoriesList = (categories || []).filter(c => c && c.id && c.id !== 'all' && c.isFeatured !== false);

  const handleNavClick = (cat) => {
    setActiveCategory(cat);
    setMobileMenuOpen(false);
    navigate(`/category/${cat}`);
  };

  return (
    <header className="fk-header-wrap" style={{ position: 'sticky', top: 0, zIndex: 900 }}>
      {/* 1. FLIPKART SIGNATURE ROYAL BLUE MAIN NAVBAR */}
      <div style={{
        backgroundColor: '#2874f0',
        color: '#ffffff',
        height: '62px',
        display: 'flex',
        alignItems: 'center',
        boxShadow: '0 1px 2px 0 rgba(0,0,0,.2)'
      }}>
        <div className="container" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          width: '100%'
        }}>
          {/* Left: Mobile Toggle & Brand Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexShrink: 0 }}>
            <button
              className="mobile-only-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
              style={{
                background: 'none',
                border: 'none',
                color: '#ffffff',
                cursor: 'pointer',
                padding: '4px',
                display: 'none',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Brand Logo with Flipkart Plus Style Tag */}
            <Link 
              to="/" 
              style={{
                textDecoration: 'none',
                display: 'flex',
                flexDirection: 'column',
                lineHeight: 1
              }}
            >
              <span style={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '1.28rem',
                fontWeight: 800,
                color: '#ffffff',
                letterSpacing: '-0.02em',
                fontStyle: 'italic'
              }}>
                {storeSettings?.storeName || 'M.L. Jewellers'}
              </span>
              <span style={{
                fontSize: '0.68rem',
                color: '#ffffff',
                fontStyle: 'italic',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
                marginTop: '2px'
              }}>
                Explore <span style={{ color: '#ffe500', fontWeight: 800 }}>Plus ★</span>
              </span>
            </Link>
          </div>

          {/* Center: Flipkart Wide White Search Bar */}
          <div 
            ref={searchContainerRef}
            className="fk-desktop-search" 
            style={{
              flex: 1,
              maxWidth: '560px',
              position: 'relative'
            }}
          >
            <form 
              onSubmit={handleSearchSubmit}
              style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#ffffff',
                borderRadius: '2px',
                boxShadow: '0 2px 4px 0 rgba(0,0,0,.23)',
                padding: '0 12px',
                height: '38px',
                width: '100%'
              }}
            >
              <input
                type="text"
                placeholder="Search for gold jewellery, coins, silverware, utensils and more..."
                value={searchQuery}
                onFocus={() => setIsSearchFocused(true)}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setIsSearchFocused(true);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: '#212121',
                  fontSize: '0.88rem',
                  width: '100%',
                  fontFamily: 'inherit'
                }}
              />

              {searchQuery ? (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#878787',
                    cursor: 'pointer',
                    padding: '0 4px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <X size={16} />
                </button>
              ) : null}

              <button
                type="submit"
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  marginLeft: '4px',
                  color: '#2874f0'
                }}
                title="Search"
              >
                <Search size={18} color="#2874f0" />
              </button>
            </form>

            {/* Desktop Autocomplete Dropdown */}
            {isSearchFocused && searchQuery.trim().length > 0 && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                left: 0,
                right: 0,
                maxHeight: '400px',
                overflowY: 'auto',
                backgroundColor: '#ffffff',
                border: '1px solid #e0e0e0',
                borderRadius: '2px',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                zIndex: 1000,
                padding: '0.4rem 0'
              }}>
                <div style={{
                  padding: '6px 14px',
                  fontSize: '0.72rem',
                  color: '#878787',
                  borderBottom: '1px solid #f0f0f0',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  <span>Found {matchingJewels.length} matching items</span>
                  <span style={{ color: '#2874f0', fontWeight: 600 }}>Press Enter to view all</span>
                </div>

                {matchingJewels.length === 0 ? (
                  <div style={{ padding: '1.25rem', textAlign: 'center', color: '#878787', fontSize: '0.85rem' }}>
                    No results for "{searchQuery}". Try "Gold", "Necklace", "Polki", "Silver".
                  </div>
                ) : (
                  matchingJewels.map((item) => {
                    const pricing = calculateProductPrice ? calculateProductPrice(item, masterMetalRates) : null;
                    const price = pricing?.finalPrice || item.price || 0;
                    return (
                      <div
                        key={item.id}
                        onMouseDown={() => handleSelectSearchResult(item.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '8px 14px',
                          cursor: 'pointer',
                          transition: 'background-color 0.15s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f3f6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                      >
                        <img
                          src={item.image || 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=120&q=80'}
                          alt={item.name}
                          style={{ width: '38px', height: '38px', borderRadius: '2px', objectFit: 'contain', backgroundColor: '#f9f9f9', border: '1px solid #f0f0f0' }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#212121', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.name}
                          </div>
                          <div style={{ fontSize: '0.78rem', color: '#2874f0', fontWeight: 700 }}>
                            {formatINR(price)}
                            <span style={{ color: '#878787', fontWeight: 'normal', marginLeft: '6px', fontSize: '0.72rem' }}>
                              {item.metal || '22K Gold'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Right: Flipkart Action Links */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexShrink: 0 }} className="fk-nav-actions">
            
            {/* Desktop Download App Button */}
            <a
              href="/mljewellers-android.apk"
              download="mljewellers-android.apk"
              className="fk-desktop-only"
              style={{
                backgroundColor: '#ffe500',
                color: '#0d2346',
                border: 'none',
                padding: '6px 14px',
                borderRadius: '2px',
                fontWeight: 800,
                fontSize: '0.86rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                textDecoration: 'none',
                boxShadow: '0 1px 3px rgba(0,0,0,.2)',
                whiteSpace: 'nowrap'
              }}
              title="Download Official M.L. Jewellers Android App (.APK)"
            >
              <Smartphone size={15} />
              <span>Download App</span>
            </a>

            {/* Customer Account / Login Button */}
            {isCustomerLoggedIn ? (
              <div 
                style={{ position: 'relative' }} 
                className="fk-desktop-only"
                onMouseEnter={() => setCustomerDropdownOpen(true)}
                onMouseLeave={() => setCustomerDropdownOpen(false)}
              >
                <button
                  onClick={() => navigate('/account')}
                  style={{
                    backgroundColor: '#ffffff',
                    color: '#2874f0',
                    border: '1px solid #dbdbdb',
                    padding: '5px 14px',
                    borderRadius: '2px',
                    fontWeight: 700,
                    fontSize: '0.88rem',
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px 0 rgba(0,0,0,.15)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <span>👤</span>
                  <span>{currentCustomer?.name ? currentCustomer.name.split(' ')[0] : 'My Account'}</span>
                  <ChevronDown size={14} />
                </button>

                {customerDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    width: '220px',
                    backgroundColor: '#ffffff',
                    borderRadius: '2px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                    border: '1px solid #e0e0e0',
                    padding: '6px 0',
                    zIndex: 9999,
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <div style={{ padding: '8px 14px', borderBottom: '1px solid #f0f0f0', fontSize: '0.78rem', color: '#6b7280' }}>
                      Customer Account <strong style={{ color: '#111827', display: 'block' }}>+91 {currentCustomer?.phone}</strong>
                    </div>
                    <button
                      onClick={() => { setCustomerDropdownOpen(false); navigate('/account'); }}
                      style={{ background: 'none', border: 'none', padding: '9px 14px', textAlign: 'left', color: '#2874f0', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f3f6'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                    >
                      📦 My Orders & Tracking
                    </button>
                    <button
                      onClick={() => { setCustomerDropdownOpen(false); navigate('/wishlist'); }}
                      style={{ background: 'none', border: 'none', padding: '9px 14px', textAlign: 'left', color: '#333333', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f3f6'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                    >
                      ❤️ Saved Jewels ({wishlist.length})
                    </button>
                    <button
                      onClick={() => { setCustomerDropdownOpen(false); navigate('/account'); }}
                      style={{ background: 'none', border: 'none', padding: '9px 14px', textAlign: 'left', color: '#333333', fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #f0f0f0' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f3f6'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                    >
                      ⚙️ Profile & PIN Settings
                    </button>
                    <button
                      onClick={() => { setCustomerDropdownOpen(false); logoutCustomer(); }}
                      style={{ background: 'none', border: 'none', padding: '9px 14px', textAlign: 'left', color: '#d32f2f', fontSize: '0.84rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fff5f5'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                    >
                      🚪 Logout Customer
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                className="fk-desktop-only"
                onClick={() => openCustomerAuthModal('login')}
                style={{
                  backgroundColor: '#ffffff',
                  color: '#2874f0',
                  border: '1px solid #dbdbdb',
                  padding: '5px 20px',
                  borderRadius: '2px',
                  fontWeight: 700,
                  fontSize: '0.92rem',
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px 0 rgba(0,0,0,.15)',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                title="Customer Sign In with Mobile Number & PIN"
              >
                <span>👤</span>
                <span>Login</span>
              </button>
            )}

            {/* Desktop Admin Portal Quick Badge (if staff authenticated) */}
            {isAdminAuthenticated && (
              <button
                className="fk-desktop-only"
                onClick={() => navigate('/admin')}
                style={{
                  backgroundColor: '#ffe500',
                  color: '#0d2346',
                  border: '1px solid #ffd700',
                  padding: '5px 12px',
                  borderRadius: '2px',
                  fontWeight: 700,
                  fontSize: '0.84rem',
                  cursor: 'pointer',
                  boxShadow: '0 1px 3px 0 rgba(0,0,0,.18)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s ease'
                }}
                title="Go to Admin Management Panel"
              >
                <span>👑</span>
                <span>Admin</span>
              </button>
            )}

            {/* "More" Dropdown */}
            <div 
              style={{ position: 'relative' }} 
              className="fk-desktop-only"
              onMouseEnter={() => setMoreDropdownOpen(true)}
              onMouseLeave={() => setMoreDropdownOpen(false)}
            >
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#ffffff',
                  fontSize: '0.92rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  whiteSpace: 'nowrap'
                }}
              >
                More <ChevronDown size={14} />
              </button>

              {moreDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  width: '210px',
                  backgroundColor: '#ffffff',
                  borderRadius: '2px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                  border: '1px solid #e0e0e0',
                  padding: '6px 0',
                  zIndex: 999,
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  {isAdminAuthenticated ? (
                    <>
                      <button
                        onClick={() => { setMoreDropdownOpen(false); navigate('/admin'); }}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: '10px 16px',
                          textAlign: 'left',
                          color: '#2874f0',
                          fontSize: '0.85rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          borderBottom: '1px solid #f0f0f0'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f3f6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                      >
                        👑 Admin Dashboard
                      </button>
                      <button
                        onClick={() => { setMoreDropdownOpen(false); logoutAdmin(); }}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: '8px 16px',
                          textAlign: 'left',
                          color: '#d32f2f',
                          fontSize: '0.82rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          borderBottom: '1px solid #f0f0f0'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fff5f5'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                      >
                        🚪 Logout Admin
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => { setMoreDropdownOpen(false); navigate('/admin'); }}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '10px 16px',
                        textAlign: 'left',
                        color: '#2874f0',
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        borderBottom: '1px solid #f0f0f0'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f3f6'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                    >
                      👑 Seller Hub & Admin Desk
                    </button>
                  )}
                  <a
                    href="/mljewellers-android.apk"
                    download="mljewellers-android.apk"
                    onClick={() => setMoreDropdownOpen(false)}
                    style={{
                      background: '#fefce8',
                      border: 'none',
                      borderBottom: '1px solid #fef08a',
                      padding: '10px 16px',
                      textAlign: 'left',
                      color: '#0d2346',
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      textDecoration: 'none'
                    }}
                  >
                    <Download size={15} color="#ca8a04" />
                    <span>📱 Download Android App</span>
                  </a>
                  <button
                    onClick={() => { setMoreDropdownOpen(false); navigate('/wastage-calculator'); }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '10px 16px',
                      textAlign: 'left',
                      color: '#212121',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f3f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                  >
                    <Scale size={15} color="#2874f0" /> 0% Wastage Calculator
                  </button>
                  <button
                    onClick={() => { setMoreDropdownOpen(false); navigate('/wastage-rate-card'); }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '10px 16px',
                      textAlign: 'left',
                      color: '#212121',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f3f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                  >
                    <Sparkles size={15} color="#ca8a04" /> Wastage Rate Card
                  </button>
                  <button
                    onClick={() => { setMoreDropdownOpen(false); navigate('/b2b-club'); }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '10px 16px',
                      textAlign: 'left',
                      color: '#212121',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f3f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                  >
                    <Gem size={15} color="#d97706" /> 24KT B2B Royal Club
                  </button>
                  <button
                    onClick={() => { setMoreDropdownOpen(false); navigate('/wishlist'); }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '10px 16px',
                      textAlign: 'left',
                      color: '#212121',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f3f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                  >
                    <Heart size={15} color="#2874f0" /> My Wishlist ({wishlist.length})
                  </button>
                  <button
                    onClick={() => { setMoreDropdownOpen(false); navigate('/about'); }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '10px 16px',
                      textAlign: 'left',
                      color: '#212121',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f3f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                  >
                    <ShieldCheck size={15} color="#2874f0" /> 24x7 Customer Care
                  </button>
                </div>
              )}
            </div>

            {/* Wishlist Icon Button (for quick access) */}
            <button
              onClick={() => navigate('/wishlist')}
              title="Wishlist"
              style={{
                background: 'none',
                border: 'none',
                color: '#ffffff',
                cursor: 'pointer',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                padding: '4px'
              }}
            >
              <Heart size={20} color={wishlist.length > 0 ? '#ffe500' : '#ffffff'} fill={wishlist.length > 0 ? '#ffe500' : 'none'} />
              {wishlist.length > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-6px',
                  backgroundColor: '#ff6161',
                  color: '#ffffff',
                  fontSize: '0.62rem',
                  fontWeight: 800,
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #ffffff'
                }}>
                  {wishlist.length}
                </span>
              )}
            </button>

            {/* Flipkart "Cart" Button */}
            <button
              onClick={() => navigate('/cart')}
              style={{
                background: 'none',
                border: 'none',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer',
                fontSize: '0.94rem',
                fontWeight: 600,
                position: 'relative',
                padding: '4px 6px'
              }}
            >
              <ShoppingCart size={20} color="#ffffff" />
              <span className="fk-desktop-only">Cart</span>
              {cartCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-3px',
                  left: '12px',
                  backgroundColor: '#ffe500',
                  color: '#2874f0',
                  fontSize: '0.65rem',
                  fontWeight: 800,
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #2874f0'
                }}>
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Permanent Flipkart-Style Search Bar (Always visible on phones) */}
      <div className="fk-mobile-search-bar" style={{
        backgroundColor: '#2874f0',
        padding: '0 12px 10px',
        display: 'none',
        position: 'relative'
      }}>
        <form onSubmit={handleSearchSubmit} style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#ffffff',
          borderRadius: '4px',
          padding: '6px 12px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.18)'
        }}>
          <Search size={17} color="#717478" style={{ marginRight: '8px', flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Search for gold jewellery, coins, silverware..."
            value={searchQuery}
            onFocus={() => setIsSearchFocused(true)}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setIsSearchFocused(true);
            }}
            style={{
              width: '100%',
              border: 'none',
              outline: 'none',
              fontSize: '0.86rem',
              color: '#212121',
              fontFamily: 'inherit'
            }}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              style={{ background: 'none', border: 'none', color: '#878787', padding: '0 4px', cursor: 'pointer' }}
            >
              <X size={16} />
            </button>
          )}
        </form>

        {/* Mobile Live Autocomplete Dropdown */}
        {isSearchFocused && searchQuery.trim().length > 0 && matchingJewels.length > 0 && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 2px)',
            left: '12px',
            right: '12px',
            backgroundColor: '#ffffff',
            boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
            borderRadius: '4px',
            zIndex: 1000,
            overflow: 'hidden',
            maxHeight: '320px',
            overflowY: 'auto'
          }}>
            {matchingJewels.map(item => {
              const pricing = calculateProductPrice ? calculateProductPrice(item, masterMetalRates) : null;
              const price = pricing?.finalPrice || item.price || 0;
              return (
                <div
                  key={item.id}
                  onClick={() => handleSelectSearchResult(item.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 12px',
                    borderBottom: '1px solid #f1f5f9',
                    cursor: 'pointer'
                  }}
                >
                  <img
                    src={item.image || 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=120&q=80'}
                    alt={item.name}
                    style={{ width: '36px', height: '36px', objectFit: 'contain', borderRadius: '2px', backgroundColor: '#fafafa' }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#212121', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.name}
                    </div>
                    <div style={{ fontSize: '0.74rem', color: '#2874f0', fontWeight: 700 }}>
                      {formatINR(price)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 2. FLIPKART SIGNATURE WHITE SUB-NAVIGATION CATEGORY BAR */}
      <div className="fk-subnav-bar" style={{
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #f0f0f0',
        boxShadow: '0 1px 1px 0 rgba(0,0,0,.08)',
        height: '42px',
        display: 'flex',
        alignItems: 'center'
      }}>
        <div className="container" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1.5rem',
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          width: '100%'
        }}>
          {/* All Jewels */}
          <button
            onClick={() => handleNavClick('all')}
            style={{
              background: 'none',
              border: 'none',
              borderBottom: activeCategory === 'all' ? '2px solid #2874f0' : '2px solid transparent',
              color: activeCategory === 'all' ? '#2874f0' : '#212121',
              fontWeight: activeCategory === 'all' ? 700 : 500,
              fontSize: '0.85rem',
              cursor: 'pointer',
              padding: '10px 4px',
              transition: 'all 0.15s ease'
            }}
          >
            All Jewellery
          </button>

          {/* Dynamic Categories */}
          {activeCategoriesList.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleNavClick(cat.id)}
              style={{
                background: 'none',
                border: 'none',
                borderBottom: activeCategory === cat.id ? '2px solid #2874f0' : '2px solid transparent',
                color: activeCategory === cat.id ? '#2874f0' : '#212121',
                fontWeight: activeCategory === cat.id ? 700 : 500,
                fontSize: '0.85rem',
                cursor: 'pointer',
                padding: '10px 4px',
                transition: 'all 0.15s ease'
              }}
            >
              {cat.name}
            </button>
          ))}

          {/* Live Bullion Rates Link */}
          <button
            onClick={() => navigate('/live-rates')}
            style={{
              background: 'none',
              border: 'none',
              color: '#d97706',
              fontWeight: 700,
              fontSize: '0.85rem',
              cursor: 'pointer',
              padding: '10px 4px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            ● Live Gold & Silver Rates
          </button>

          {/* Calculator Link */}
          <button
            onClick={() => navigate('/calculator')}
            style={{
              background: 'none',
              border: 'none',
              color: '#16a34a',
              fontWeight: 600,
              fontSize: '0.85rem',
              cursor: 'pointer',
              padding: '10px 4px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            ⚖️ Price Calculator
          </button>

          {/* Store Locator Link */}
          <button
            onClick={() => navigate('/store-locator')}
            style={{
              background: 'none',
              border: 'none',
              color: '#2563eb',
              fontWeight: 600,
              fontSize: '0.85rem',
              cursor: 'pointer',
              padding: '10px 4px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            📍 Store Locator
          </button>

          {/* About */}
          <button
            onClick={() => navigate('/about')}
            style={{
              background: 'none',
              border: 'none',
              color: '#878787',
              fontWeight: 500,
              fontSize: '0.85rem',
              cursor: 'pointer',
              padding: '10px 4px',
              marginLeft: 'auto'
            }}
          >
            About Us
          </button>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      {mobileMenuOpen && (
        <div style={{
          backgroundColor: '#ffffff',
          borderBottom: '2px solid #2874f0',
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
        }}>
          {/* Prominent Android App Download Card */}
          <a
            href="/mljewellers-android.apk"
            download="mljewellers-android.apk"
            onClick={() => setMobileMenuOpen(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: 'linear-gradient(135deg, #0d2346 0%, #1e3a8a 100%)',
              color: '#ffffff',
              fontSize: '0.9rem',
              fontWeight: 700,
              padding: '10px 12px',
              borderRadius: '6px',
              textDecoration: 'none',
              boxShadow: '0 2px 6px rgba(13, 35, 70, 0.25)',
              border: '1px solid #ffe500',
              marginBottom: '6px'
            }}
          >
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '6px',
              backgroundColor: '#ffe500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Smartphone size={18} color="#0d2346" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: '#ffffff', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>Download Android App</span>
                <span style={{ backgroundColor: '#22c55e', color: '#fff', fontSize: '0.62rem', padding: '1px 5px', borderRadius: '3px', fontWeight: 800 }}>APK</span>
              </div>
              <div style={{ fontSize: '0.7rem', color: '#93c5fd', fontWeight: 400 }}>
                24 MB • Live Rates & Instant Orders
              </div>
            </div>
            <Download size={16} color="#ffe500" />
          </a>
          <button
            onClick={() => handleNavClick('all')}
            style={{
              textAlign: 'left',
              background: 'none',
              border: 'none',
              color: activeCategory === 'all' ? '#2874f0' : '#212121',
              fontSize: '0.95rem',
              fontWeight: 600,
              padding: '8px 0',
              borderBottom: '1px solid #f0f0f0'
            }}
          >
            All Jewellery
          </button>

          {activeCategoriesList.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleNavClick(cat.id)}
              style={{
                textAlign: 'left',
                background: 'none',
                border: 'none',
                color: activeCategory === cat.id ? '#2874f0' : '#212121',
                fontSize: '0.95rem',
                fontWeight: 600,
                padding: '8px 0',
                borderBottom: '1px solid #f0f0f0'
              }}
            >
              {cat.name}
            </button>
          ))}

          <button
            onClick={() => { setMobileMenuOpen(false); navigate('/live-rates'); }}
            style={{
              textAlign: 'left',
              background: 'none',
              border: 'none',
              color: '#d97706',
              fontSize: '0.95rem',
              fontWeight: 600,
              padding: '8px 0',
              borderBottom: '1px solid #f0f0f0'
            }}
          >
            📊 Live Gold & Silver Rates
          </button>

          <button
            onClick={() => { setMobileMenuOpen(false); navigate('/wastage-calculator'); }}
            style={{
              textAlign: 'left',
              background: 'none',
              border: 'none',
              color: '#388e3c',
              fontSize: '0.95rem',
              fontWeight: 600,
              padding: '8px 0',
              borderBottom: '1px solid #f0f0f0'
            }}
          >
            ⚖️ 0% Wastage Calculator
          </button>

          <button
            onClick={() => { setMobileMenuOpen(false); navigate('/wastage-rate-card'); }}
            style={{
              textAlign: 'left',
              background: 'none',
              border: 'none',
              color: '#ca8a04',
              fontSize: '0.95rem',
              fontWeight: 600,
              padding: '8px 0',
              borderBottom: '1px solid #f0f0f0'
            }}
          >
            📋 24KT Wastage Rate Card
          </button>

          <button
            onClick={() => { setMobileMenuOpen(false); navigate('/b2b-club'); }}
            style={{
              textAlign: 'left',
              background: 'none',
              border: 'none',
              color: '#b45309',
              fontSize: '0.95rem',
              fontWeight: 600,
              padding: '8px 0',
              borderBottom: '1px solid #f0f0f0'
            }}
          >
            👑 24KT B2B Wholesale Club
          </button>

          <button
            onClick={() => { setMobileMenuOpen(false); navigate('/calculator'); }}
            style={{
              textAlign: 'left',
              background: 'none',
              border: 'none',
              color: '#16a34a',
              fontSize: '0.95rem',
              fontWeight: 600,
              padding: '8px 0',
              borderBottom: '1px solid #f0f0f0'
            }}
          >
            🧮 Jewellery Price Calculator
          </button>

          <button
            onClick={() => { setMobileMenuOpen(false); navigate('/store-locator'); }}
            style={{
              textAlign: 'left',
              background: 'none',
              border: 'none',
              color: '#2563eb',
              fontSize: '0.95rem',
              fontWeight: 600,
              padding: '8px 0',
              borderBottom: '1px solid #f0f0f0'
            }}
          >
            📍 Store Locator & Showrooms
          </button>

          <button
            onClick={() => { setMobileMenuOpen(false); navigate('/about'); }}
            style={{
              textAlign: 'left',
              background: 'none',
              border: 'none',
              color: '#555555',
              fontSize: '0.95rem',
              fontWeight: 600,
              padding: '8px 0',
              borderBottom: '1px solid #f0f0f0'
            }}
          >
            🏢 About Heritage & Hallmark
          </button>

          {/* Mobile Customer Login / Profile Options */}
          {isCustomerLoggedIn ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', margin: '6px 0', padding: '8px 0', borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>
              <button
                onClick={() => { setMobileMenuOpen(false); navigate('/account'); }}
                style={{
                  textAlign: 'left',
                  background: '#f0f5ff',
                  border: '1px solid #dbeafe',
                  color: '#2874f0',
                  fontSize: '0.92rem',
                  fontWeight: 700,
                  padding: '10px 12px',
                  borderRadius: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                👤 My Account (+91 {currentCustomer?.phone})
              </button>
              <button
                onClick={() => { setMobileMenuOpen(false); logoutCustomer(); }}
                style={{
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  color: '#ef4444',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  padding: '6px 12px'
                }}
              >
                🚪 Sign Out Customer
              </button>
            </div>
          ) : (
            <button
              onClick={() => { setMobileMenuOpen(false); openCustomerAuthModal('login'); }}
              style={{
                textAlign: 'left',
                background: '#2874f0',
                border: 'none',
                color: '#ffffff',
                fontSize: '0.92rem',
                fontWeight: 700,
                padding: '10px 12px',
                borderRadius: '2px',
                margin: '8px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>🔑 Customer Login (Mobile & PIN)</span>
            </button>
          )}

          {isAdminAuthenticated ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px' }}>
              <button
                onClick={() => { setMobileMenuOpen(false); navigate('/admin'); }}
                style={{
                  textAlign: 'left',
                  background: '#ffe500',
                  border: 'none',
                  color: '#0d2346',
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  padding: '10px 12px',
                  borderRadius: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                👑 Open Admin Panel
              </button>
              <button
                onClick={() => { setMobileMenuOpen(false); logoutAdmin(); }}
                style={{
                  textAlign: 'left',
                  background: '#fff0f0',
                  border: '1px solid #ffcdd2',
                  color: '#d32f2f',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  padding: '8px 12px',
                  borderRadius: '2px'
                }}
              >
                🚪 Logout Admin Session
              </button>
            </div>
          ) : (
            <button
              onClick={() => { setMobileMenuOpen(false); navigate('/admin'); }}
              style={{
                textAlign: 'left',
                background: '#e8f0fe',
                border: 'none',
                color: '#2874f0',
                fontSize: '0.95rem',
                fontWeight: 700,
                padding: '10px 12px',
                borderRadius: '2px',
                marginTop: '4px'
              }}
            >
              🔐 Seller Hub & Admin Desk
            </button>
          )}
        </div>
      )}

      <style>{`
        @media (max-width: 860px) {
          .fk-desktop-search, .fk-desktop-only {
            display: none !important;
          }
          .mobile-only-btn {
            display: inline-flex !important;
          }
          .fk-mobile-search-bar {
            display: block !important;
          }
        }
      `}</style>
    </header>
  );
}
